import { happyApi, type HappyRow } from '../../api/happyCode';
import { customerCareApi, type CareContact, type CareIssue } from '../../api/customerCare';
import type { RoleProfile } from '../../utils/roleProfile';
import type { Route } from '../../state/route.svelte';

// Shared, identity-scoped options for the coordinator/care call queue so the
// home tiles and the queue card read one cache. Care sections follow the
// existing rule: support-only teams for the care profile (team null), the CRM
// selector otherwise.
export interface CallScope { profile: RoleProfile; userId?: number; vertical: number | 'all'; accessKey: string; happyAccess: boolean }
export function callQueries(scope: CallScope) {
  const enabled = scope.profile === 'coordination' || scope.profile === 'care';
  const team = scope.profile === 'care' ? null : scope.vertical === 'all' ? null : scope.vertical;
  const key = (name: string) => [name, scope.profile, scope.userId, scope.vertical, scope.accessKey];
  return {
    happy: { queryKey: key('my-day-happy-queue'), enabled: enabled && scope.happyAccess, queryFn: happyApi.queue, staleTime: 60000 },
    fixes: { queryKey: [...key('my-day-fix-confirmations'), scope.profile === 'coordination' ? 'sales-only' : 'support'], enabled: enabled && (scope.profile === 'care' || scope.happyAccess), queryFn: () => scope.profile === 'coordination' ? happyApi.queueFixes(team ?? undefined, 10) : customerCareApi.list('need_to_speak', 1, team, 3, 30), staleTime: 60000 },
    callbacks: { queryKey: key('my-day-callbacks'), enabled, queryFn: () => customerCareApi.list('contact_calls', 1, team, 3, 30), staleTime: 60000 }
  };
}

export type CallKind = 'negative' | 'day7' | 'fix' | 'callback';
export interface CallItem { key: string; kind: CallKind; label: string; name: string; context: string; phone: string; target: Route }
const kindLabels: Record<CallKind, string> = { negative: 'Unhappy customer · call today', day7: 'Day-7 happy call', fix: 'Fix confirmation', callback: 'Callback' };

// Order: unhappy customers first, then day-7 calls, fix confirmations, callbacks.
export function callItems(happy: { negatives?: HappyRow[]; due_calls?: HappyRow[] } | undefined, fixes: CareIssue[], callbacks: CareContact[]): CallItem[] {
  const happyItem = (row: HappyRow, kind: CallKind): CallItem => ({
    key: `${kind}:${row.id}`, kind, label: kindLabels[kind], name: row.customer_name || row.name || row.site || `Order ${row.order_number ?? row.id}`,
    context: [row.site, row.order_number && `Order ${row.order_number}`, row.technician && `installed by ${row.technician}`, row.comment && `“${row.comment}”`].filter(Boolean).join(' · '),
    phone: row.verified_phone || row.phone || '', target: { tab: 'happy-code-queue' } as Route
  });
  return [
    ...(happy?.negatives ?? []).map(row => happyItem(row, 'negative')),
    ...(happy?.due_calls ?? []).map(row => happyItem(row, 'day7')),
    ...fixes.map(issue => ({
      key: `fix:${issue.id}`, kind: 'fix' as const, label: kindLabels.fix, name: issue.customer_name || issue.company_name || issue.reporter_name || `Case #${issue.id}`,
      context: [`Case #${issue.id} · ${issue.title}`, issue.team_name, issue.resolving_technician_name && `fixed by ${issue.resolving_technician_name}`].filter(Boolean).join(' · '),
      phone: issue.reporter_phone || '', target: { tab: 'issues', id: issue.id } as Route
    })),
    ...callbacks.map(person => ({
      key: `callback:${person.id}`, kind: 'callback' as const, label: kindLabels.callback, name: `${person.first_name} ${person.last_name}`.trim() || `Contact #${person.id}`,
      context: [person.next_action || person.reason, person.team_name].filter(Boolean).join(' · '),
      phone: person.phone || '', target: { tab: 'people', id: person.id } as Route
    }))
  ];
}
export const callPrompts: Record<CallKind, string> = {
  negative: 'Listen first. What went wrong, and what would make it right? Open a same-day case from the queue.',
  day7: 'Is everything working as expected? Anything the installer should come back for? If happy, share the review link.',
  fix: 'Is the problem fixed for you? Confirm before the case is closed.',
  callback: 'They asked to be called. Check the note, then record what was agreed.'
};
